import { ref, onMounted, onUnmounted } from 'vue'
// 内联简单的工具函数，避免混合导入警告
// import { 
//   isPWASupported, 
//   isPWAInstalled, 
//   isOnline,
//   checkForUpdate,
//   getNetworkInfo,
//   promptInstallPWA
// } from '@/utils/pwa'

/**
 * 检查是否支持PWA（内联函数，避免混合导入）
 */
function isPWASupported() {
  return 'serviceWorker' in navigator
}

/**
 * 检查是否已安装PWA（内联函数，避免混合导入）
 */
function isPWAInstalled() {
  // 检查是否在standalone模式下运行（已安装）
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true
  }
  // iOS Safari检查
  if (window.navigator.standalone === true) {
    return true
  }
  return false
}

/**
 * 检查网络状态（内联函数，避免混合导入）
 */
function isOnline() {
  return navigator.onLine
}

/**
 * 获取网络状态信息（内联函数，避免混合导入）
 */
function getNetworkInfo() {
  if ('connection' in navigator) {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection
    return {
      effectiveType: connection.effectiveType,
      downlink: connection.downlink,
      rtt: connection.rtt,
      saveData: connection.saveData
    }
  }
  return null
}

/**
 * 检查更新（内联函数，避免混合导入）
 */
function checkForUpdate() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistration().then((registration) => {
      if (registration) {
        registration.update()
      }
    })
  }
}

/**
 * PWA功能组合式API
 * @returns {Object} PWA相关状态和方法
 */
export function usePWA() {
  const isSupported = ref(isPWASupported())
  const isInstalled = ref(isPWAInstalled())
  const online = ref(isOnline())
  const networkInfo = ref(getNetworkInfo())
  const deferredPrompt = ref(null)
  const canInstall = ref(false)

  // 监听安装提示事件
  const handleBeforeInstallPrompt = (e) => {
    e.preventDefault()
    deferredPrompt.value = e
    canInstall.value = true
  }

  // 监听网络状态变化
  const handleOnline = () => {
    online.value = true
    networkInfo.value = getNetworkInfo()
  }

  const handleOffline = () => {
    online.value = false
  }

  // 监听网络信息变化
  const handleConnectionChange = () => {
    networkInfo.value = getNetworkInfo()
  }

  // 安装PWA
  const install = async () => {
    if (deferredPrompt.value) {
      deferredPrompt.value.prompt()
      const { outcome } = await deferredPrompt.value.userChoice
      deferredPrompt.value = null
      canInstall.value = false
      return outcome === 'accepted'
    }
    return false
  }

  // 检查更新
  const update = () => {
    checkForUpdate()
  }

  onMounted(() => {
    if (isSupported.value) {
      // 监听安装提示
      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      
      // 监听网络状态
      window.addEventListener('online', handleOnline)
      window.addEventListener('offline', handleOffline)
      
      // 监听网络信息变化
      if ('connection' in navigator) {
        const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection
        if (connection) {
          connection.addEventListener('change', handleConnectionChange)
        }
      }
    }
  })

  onUnmounted(() => {
    if (isSupported.value) {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      
      if ('connection' in navigator) {
        const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection
        if (connection) {
          connection.removeEventListener('change', handleConnectionChange)
        }
      }
    }
  })

  return {
    // 状态
    isSupported,
    isInstalled,
    online,
    networkInfo,
    canInstall,
    // 方法
    install,
    update
  }
}

